import { useTranslation } from '@rhino-project/core/i18n';
import {
  useAuthIdentities,
  useUnlinkAuthIdentity,
  useApiKeysEnabled
} from '@rhino-project/core/queries';
import { Button, Card, CardContent, Typography } from '@heroui/react';
import { ChangePassword } from './ChangePassword';
import { PasskeysSettings } from './PasskeysSettings';
import { ApiKeysSettings } from './ApiKeysSettings';

interface AuthIdentityRow {
  id: number;
  provider: string;
  created_at: string;
  can_unlink: boolean;
}

interface AuthIdentitiesPayload {
  password_set: boolean;
  identities: AuthIdentityRow[];
}

const PROVIDER_LABELS: Record<string, string> = {
  google_oauth2: 'Google',
  github: 'GitHub',
  facebook: 'Facebook',
  azure_oauth2: 'Microsoft',
  auth0: 'Auth0'
};

const providerLabel = (p: string) => PROVIDER_LABELS[p] ?? p;

export const SignInSettings = () => {
  const { t, locale } = useTranslation();
  // Data is prefetched by the route loader (prefetchAuthIdentities +
  // prefetchPasskeys), so this useQuery hits a warm cache and renders without a
  // loading spinner. The `data ?? default` fallback keeps a non-prefetched mount
  // rendering an empty state rather than crashing.
  const { data } = useAuthIdentities();
  const { mutate: unlink, isPending: unlinking } = useUnlinkAuthIdentity();
  const apiKeysEnabled = useApiKeysEnabled();

  const payload = (data ?? {
    password_set: false,
    identities: []
  }) as AuthIdentitiesPayload;

  const handleUnlink = (id: number) => unlink(id);

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-2">
        <Typography.Heading level={4} weight="medium">
          {t('rhino.frontend.messages.email_and_password')}
        </Typography.Heading>
        {payload.password_set ? (
          <ChangePassword />
        ) : (
          <Card>
            <CardContent>
              <span className="text-sm text-muted">
                {t('rhino.frontend.messages.connected_account_notice')}
              </span>
            </CardContent>
          </Card>
        )}
      </section>

      <PasskeysSettings />

      <section className="flex flex-col gap-2">
        <Typography.Heading level={4} weight="medium">
          {t('rhino.frontend.messages.connected_accounts')}
        </Typography.Heading>
        {payload.identities.length === 0 && (
          <span className="text-sm text-muted">
            {t('rhino.frontend.messages.no_connected_accounts')}
          </span>
        )}
        {payload.identities.map((identity) => (
          <Card key={identity.id}>
            <CardContent className="flex flex-row items-center justify-between">
              <div className="flex flex-col gap-1">
                <span className="font-medium">
                  {providerLabel(identity.provider)}
                </span>
                <span className="text-sm text-muted">
                  {t('rhino.frontend.messages.connected')}
                  {new Date(identity.created_at).toLocaleDateString(
                    locale.replaceAll('_', '-')
                  )}
                </span>
              </div>
              <Button
                size="sm"
                variant="danger-soft"
                isDisabled={unlinking || !identity.can_unlink}
                onPress={() => handleUnlink(identity.id)}
              >
                {t('rhino.frontend.messages.unlink')}
              </Button>
            </CardContent>
          </Card>
        ))}
      </section>

      {apiKeysEnabled && <ApiKeysSettings />}
    </div>
  );
};
